import { StringUtils } from '../../utils/StringUtils';
import '../../helper/animationLoops';
import { GlobalImport } from '../../utils/GlobalImport';
import { BlockType } from '../../@types/BlockType';

const Blockly = GlobalImport.getBlockly();
export class CodeSetting {
  static ATTR = 'ATTR';
  static IN = 'IN';
  static COLOUR = 'COLOUR';
  // static readonly ESCAPECODE = '_escape_'; // position/rotation/scaleなどの属性に値が入らない、その他の属性はいけるので_escape_を頭につけてDOMを作ってHTML文字列から除去することで対処

  static getSubAttributeCode(block: BlockType): string {
    return Blockly.JavaScript.valueToCode(block, this.ATTR, Blockly.JavaScript.ORDER_NONE);
  }

  static getSubstatementCode(block: BlockType): string {
    const input = Blockly.JavaScript.statementToCode(block, this.IN, Blockly.JavaScript.ORDER_NONE);
    return input ? `\n${input}` : '';
  }

  static getInputValue(block: BlockType, fieldName: string): string {
    let code = Blockly.JavaScript.valueToCode(block, fieldName, Blockly.JavaScript.ORDER_NONE);
    code = code.replace(/[${]/g, '').replace(/}/g, '');
    return code;
  }

  static getFieldValueWithoutTemplateString(block: BlockType, fieldName: string): string {
    let value = block.getFieldValue(fieldName);
    value = value ? value.trim() : '';
    value = StringUtils.toSingleByteNumber(value);
    if (StringUtils.isStatementInput(value)) {
      value = StringUtils.trimFirstEqualChar(value);
    }
    return StringUtils.htmlEncode(value);
  }

  static getFieldValue(block: BlockType, fieldName: string): string {
    let value = block.getFieldValue(fieldName);
    value = value ? value.trim() : '';
    value = StringUtils.toSingleByteNumber(value);
    if (StringUtils.isStatementInput(value)) {
      value = '${' + StringUtils.trimFirstEqualChar(value) + '}';
    }
    return StringUtils.htmlEncode(value);
  }
  static getNumberFieldValue(block: BlockType, fieldName: string): string {
    let value = block.getFieldValue(fieldName);
    value = value ? value.trim() : '0';
    value = StringUtils.toSingleByteNumber(value);
    if (StringUtils.isStatementInput(value)) {
      value = StringUtils.trimFirstEqualChar(value);
    }
    return StringUtils.htmlEncode(value);
  }

  static getFieldValueWithoutEncode(block: BlockType, fieldName: string): string {
    let value = block.getFieldValue(fieldName);
    value = value ? value.trim() : '';
    value = StringUtils.toSingleByteNumber(value);
    if (StringUtils.isStatementInput(value)) {
      value = '${' + StringUtils.trimFirstEqualChar(value) + '}';
    }
    return value;
  }

  static getColorInputValue(block: BlockType): string {
    return Blockly.JavaScript.valueToCode(block, this.COLOUR, Blockly.JavaScript.ORDER_NONE);
  }

  static getVR3FieldValues(block: BlockType, type = 'position'): string {
    const getValue = (block, fieldName): string => {
      let value = block.getFieldValue(fieldName) ?? '';
      value = StringUtils.toSingleByteNumber(value.trim());
      if (StringUtils.isStatementInput(value)) {
        value = '${' + StringUtils.trimFirstEqualChar(value) + '}';
      }
      return StringUtils.htmlEncode(value);
    };
    const x = getValue(block, type + '-x');
    const y = getValue(block, type + '-y');
    const z = getValue(block, type + '-z');
    const str = `entity.setAttribute("${type}",\`${x} ${y} ${z}\`);`;
    return str;
  }

  static getARDefaultCameraCode(block_id: string): string {
    let defaultCameraAtt = CodeSetting.getAttributeCode('id', 'ar_camera');
    defaultCameraAtt += CodeSetting.getAttributeCode('camera', '');
    const defaultCamera = CodeSetting.getElementCode('a-entity', defaultCameraAtt, block_id, '');
    return defaultCamera;
  }

  // static getElementCodeNS(
  //   tagName: string,
  //   attribute: string,
  //   subBlock: string,
  //   parent = 'parent'
  // ): string {
  //   const str = `
  // 	// ${tagName} create
  // 	{
  // 		let entity = document.createElementNS("http://www.w3.org/1999/xhtml","${tagName}");
  // 		${attribute}
  // 		${subBlock}
  // 		${parent}.append(entity);
  // 	}
  // 	`;
  //   return str;
  // }

  static getElementCode(
    tagName: string,
    attribute: string,
    block_id: string,
    subBlock: string
  ): string {
    const entityId = Math.floor(Math.random() * 1000000);
    const entityName = `entity${entityId}`;
    const parentName = `parent${entityId}`;

    let sub = '';
    sub += `let ${parentName} = parent; /* escape the parent of this element */`;
    if (subBlock && subBlock != '') {
      // サブブロックはこのエンティティを親としてAppendする
      sub += `parent = ${entityName}; /* To convey "parent element" to child elements */`;
      sub += subBlock;
    }

    const str = `
		// Create ${tagName}
		{
			let ${entityName} = document.createElement("${tagName}");
			let entity = ${entityName};
      entity.setAttribute("data-block_id", "${block_id}");
			${attribute}

			${sub}

			${parentName}.appendChild(${entityName});
			parent = ${parentName}; // reset parent
		}`;
    return str;
  }

  static getUpdateElementCode(domId: string, attribute: string): string {
    const str = `
		// Update ${domId}
		{
			let updateDom = document.getElementById( \`${domId}\`);
			let entity = updateDom;
      if (entity) {
        ${attribute}
      }
		}`;
    return str;
  }

  static getLibraryCode(path: string, attribute: string): string {
    const entityId = Math.floor(Math.random() * 1000000);
    const entityName = `entity${entityId}`;

    const str = `
        // Create ${path}
        {
            let ${entityName} = document.createElement("script");

            let entity = ${entityName};
            entity.src = '${path}';

            ${attribute}
            document.head.innerHTML += ${entityName}.outerHTML;

        }
    `;
    return str;
  }

  static getAttributeCode(attributeName: string, value: string, parent = 'entity'): string {
    const str = `${parent}.setAttribute("${attributeName}", \`${value}\`);`;
    return str;
  }

  /**
   * DocumentFragmentオブジェクトからHTML文字列を取り出す
   */
  static convertFragmentToHTMLString(fragment: any): string {
    // ダミーのdivでHTMLを取り出す
    const dummy = document.createElement('div');
    dummy.append(fragment);

    const html = dummy.innerHTML.replace(/></g, `>\n<`);

    return html;
  }
  /**
   * 属性の組み立てを実施
   * @param { name,value } attributes array of [name,value]
   * @param {*} otherAttribute att
   */
  static buildAttribute(attributes: string[][], otherAttribute = ''): string {
    let str = '';
    for (const att of attributes) {
      if (att) {
        str += CodeSetting.getAttributeCode(att[0], att[1]);
      }
    }
    str += otherAttribute;
    return str;
  }
  /**
   * Elementの内容として文字列を入れる場合に使用する。この戻り値をSubElementとして使うことでHTMLタグの入れ子の中に文字をかける。
   * @param htmlContents
   * @returns
   */
  static getTextNode(htmlContents: string): string {
    const str = `entity.innerHTML = '${htmlContents}'`;
    return str;
  }

  static getMindArDefaultCameraCode(): string {
    return `const camItem = document.createElement("a-camera");
    camItem.setAttribute("position", "0 0 0");
    camItem.setAttribute("look-controls", "enabled: false");
    document.querySelector("a-camera") ? null : entity.append(camItem);`;
  }
}
